import { Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AppGate } from "./AppGate";
import { FRONTEND_ROUTES } from "@fysaro/shared";
import { FullScreenLoader } from "@/shared/ui/components/FullScreenLoader";
import { lazyWithReload } from "./lazyWithReload";
import { PublicRoutes } from "@/modules/public/router/PublicRoutes";
import { PublicClinicSubdomainRedirect } from "@/modules/public/PublicClinicSubdomainRedirect";

const AuthRoutes = lazyWithReload(async () => ({
  default: (await import("@/modules/auth/router/AuthRoutes")).AuthRoutes,
}), "auth-routes");
const SuperadminRoutes = lazyWithReload(async () => ({
  default: (await import("@/modules/superadmin/router/SuperadminRoutes"))
    .SuperadminRoutes,
}), "superadmin-routes");
const TenantRoutes = lazyWithReload(async () => ({
  default: (await import("@/modules/tenant/router/TenantRoutes")).TenantRoutes,
}), "tenant-routes");
const OnboardingRoutes = lazyWithReload(async () => ({
  default: (await import("@/modules/tenant/onboarding/router/OnboardingRoutes"))
    .OnboardingRoutes,
}), "onboarding-routes");
const PatientRoutes = lazyWithReload(async () => ({
  default: (await import("@/modules/patient/router/PatientRoutes"))
    .PatientRoutes,
}), "patient-routes");
const PatientRecoveryPlanPage = lazyWithReload(async () => ({
  default: (await import("@/modules/public/recovery-plan/PatientRecoveryPlanPage"))
    .default,
}), "patient-recovery-plan");

export function AppRouter() {
  const hostname = window.location.hostname;
  
  // 🚀 ÚNICAMENTE subdominios dedicados a la APP (Paneles / Logins)
  const isAppSubdomain =
    hostname.startsWith("app.") ||                  // ej: app.fysaro.com o app.fysaro.local
    hostname.startsWith("app-staging.") ||          // ej: app-staging.fysaro.com o app-staging.fysaro.local
    hostname === "localhost" ||                     // Desarrollo rápido por defecto
    hostname === "127.0.0.1";
    
  return (
    <BrowserRouter>
      <Suspense fallback={<FullScreenLoader />}>
        <Routes>
          {isAppSubdomain ? (
            // =========================================================
            // 🚀 CONFIGURACIÓN PARA APPS (app.fysaro.* / app-staging.fysaro.*)
            // =========================================================
            <>
              {/* Si entran a la raí­z pura de app.fysaro.com -> Al login de clí­nicas por defecto */}
              <Route
                path="/"
                element={
                  <Navigate to={FRONTEND_ROUTES.auth.loginClinic} replace />
                }
              />

              {/* Autenticación abierta (Logins, Registro, Invitaciones) */}
              <Route path="/auth/*" element={<AuthRoutes />} />

              {/* Plan público: accesible sin sesión aunque se abra desde el host app */}
              <Route
                path="/plan/:planId"
                element={<PatientRecoveryPlanPage />}
              />

              {/* Micrositio clínica: canonical vive en el host público (fysaro.com/p/:slug) */}
              <Route
                path="/p/:slug"
                element={<PublicClinicSubdomainRedirect />}
              />

              {/* Zona Protegida por la barrera del AppGate */}
              <Route element={<AppGate />}>
                <Route path="/tenant/*" element={<TenantRoutes />} />
                <Route path="/patient/*" element={<PatientRoutes />} />
                <Route path="/superadmin/*" element={<SuperadminRoutes />} />
                <Route path="/onboarding/*" element={<OnboardingRoutes />} />
              </Route>

              {/* Catch-all para app: si meten una ruta rota, al login */}
              <Route
                path="*"
                element={
                  <Navigate to={FRONTEND_ROUTES.auth.loginClinic} replace />
                }
              />
            </>
          ) : (
            // =========================================================
            // 🌐 CONFIGURACIÓN PARA LANDING / WEB PÚBLICA
            // (fysaro.local, staging.fysaro.local, fysaro.com, etc.)
            // =========================================================
            <>
              {/* Todas las páginas de la landing (Home, Contacto, Polí­ticas...) */}
              <Route path="/*" element={<PublicRoutes />} />

              {/* Catch-all para la web de marketing: siempre a la home */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </>
          )}
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
}
